import type React from "react"
import styled, { keyframes } from "styled-components"

const breatheDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.3); opacity: 1; }
`

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const StyledFooter = styled.footer`
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(25px);
  border-top: 1px solid rgba(25, 118, 210, 0.08);
  padding: 48px 20px 32px;
  margin-top: auto;
  position: relative;
  z-index: 10;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--brand-primary), transparent);
    border-radius: 1px;
  }
`

const FooterContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 36px;
  padding: 0 20px;
`

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeUp} 1s ease forwards;
  position: relative;
`

const Logo = styled.h2`
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 32px;
  font-weight: 300;
  color: var(--brand-primary);
  letter-spacing: 6px;
  margin: 0;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
    animation: ${shimmer} 3s ease-in-out infinite;
  }
`

const LogoDot = styled.div`
  width: 6px;
  height: 6px;
  background: var(--brand-primary);
  border-radius: 50%;
  margin-top: 12px;
  animation: ${breatheDot} 4s ease-in-out infinite;
`

const FooterMessage = styled.div`
  text-align: center;
  color: #444;
  font-size: 16px;
  line-height: 1.7;
  max-width: 700px;
  animation: ${fadeUp} 1.2s ease forwards 0.2s;
  opacity: 0;
  animation-fill-mode: forwards;

  .highlight {
    color: var(--brand-primary);
    font-weight: 500;
    position: relative;
  }
  
  .secondary-highlight {
    color: #333;
    font-weight: 500;
  }
`

const FooterQuote = styled.div`
  text-align: center;
  color: var(--brand-primary);
  font-size: 15px;
  font-style: italic;
  font-weight: 400;
  animation: ${fadeUp} 1.2s ease forwards 0.4s;
  opacity: 0;
  animation-fill-mode: forwards;
  position: relative;

  &::before, &::after {
    content: '—';
    margin: 0 8px;
    opacity: 0.6;
    font-weight: 300;
  }
`

const FooterCopyright = styled.div`
  color: var(--brand-primary);
  font-size: 13px;
  letter-spacing: 1.5px;
  font-weight: 400;
  animation: ${fadeUp} 1.2s ease forwards 0.6s;
  opacity: 0;
  animation-fill-mode: forwards;
  padding-top: 24px;
  border-top: 1px solid rgba(var(--brand-primary-rgb), 0.1);
  width: 100%;
  text-align: center;
`

const Footer: React.FC = () => {
  return (
    <StyledFooter>
      <FooterContent>
        <LogoContainer>
          <Logo>DISEX</Logo>
          <LogoDot />
        </LogoContainer>

        <FooterMessage>
          En <span className="highlight">Disex</span> nos apasiona redefinir la industria textil mediante la fusión de <span className="highlight">creatividad</span>, <span className="highlight">calidad</span> y <span className="highlight">sostenibilidad</span>.<br /><br />
          Apostamos por el talento colombiano en el desarrollo de productos que se destacan por su <span className="highlight">confort y diseño</span>, actuando con ética y responsabilidad en todos nuestros procesos, impulsando el bienestar de nuestros colaboradores y aliados a través de una amplia experiencia en el sector.
        </FooterMessage>


        <FooterCopyright>© 2030 Diseños Exclusivos SAS</FooterCopyright>
      </FooterContent>
    </StyledFooter>
  )
}

export default Footer
